/**
 * The sidebar, while someone is inside a library.
 *
 * *** A FOCUS RAIL, NOT A SECOND SIDEBAR ***
 * It renders INSIDE the shell's existing dark column, in place of the
 * Library / Leaderboard navigation. One column, one header, the same on
 * every screen — the room's own controls simply take the nav's place.
 *
 * A student who came to study for three hours does not need other screens
 * one mis-click away. "All rooms" is the deliberate way out, and Leave is
 * pinned at the bottom where it is always reachable.
 *
 * *** IT READS THE SAME QUERIES AS THE ROOM ***
 * No props: this sits in the shell, several levels above the room, and
 * threading state up would mean every route in between knew about it.
 * React Query serves both from one cache, so the figures cannot disagree.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import {
  useEndBreak,
  useLeaveLibrary,
  useLibraryOccupants,
  useStartBreak,
} from '@/lib/queries'
import { countdown, hoursMinutes } from './format'
import styles from './LibraryFocusRail.module.css'

export function LibraryFocusRail() {
  const { libraryId = '' } = useParams<{ libraryId: string }>()
  const navigate = useNavigate()
  const toast = useToast()

  const { data } = useLibraryOccupants(libraryId)
  const leave = useLeaveLibrary()
  const startBreak = useStartBreak()
  const endBreak = useEndBreak()

  const me = data?.occupants.find((o) => o.is_mine) ?? null
  const iAmMentor = me?.role === 'MENTOR'

  const [breakLeft, setBreakLeft] = useState<number | null>(null)
  const onBreak = breakLeft !== null

  /*
    The displayed figure ticks between polls so the timer does not look
    frozen. The number that counts is always the server's — this only
    smooths the seconds in between, and stops entirely during a break
    because the server credits nothing then.
  */
  const [, setTick] = useState(0)
  const baseline = useRef<{ seconds: number; at: number } | null>(null)

  useEffect(() => {
    if (me) baseline.current = { seconds: me.active_seconds, at: Date.now() }
  }, [me?.active_seconds])

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTick((t) => t + 1)
      setBreakLeft((left) => (left === null ? null : Math.max(0, left - 1)))
    }, 1000)
    return () => window.clearInterval(timer)
  }, [])

  const shown = !baseline.current
    ? 0
    : onBreak
      ? baseline.current.seconds
      : baseline.current.seconds +
        Math.floor((Date.now() - baseline.current.at) / 1000)

  const handleBreak = async () => {
    try {
      const visit = await startBreak.mutateAsync()
      setBreakLeft(visit.break_seconds_left)
      toast.info('On a break. Your seat is held for 30 minutes.')
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Could not start a break.',
      )
    }
  }

  const handleResume = async () => {
    try {
      const visit = await endBreak.mutateAsync()
      setBreakLeft(visit.break_seconds_left)
      toast.success('Welcome back. Your timer is running again.')
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Could not end the break.',
      )
    }
  }

  const handleLeave = async () => {
    try {
      const result = await leave.mutateAsync()
      if (result.left) {
        const minutes = Math.round(result.active_seconds / 60)
        toast.success(
          minutes > 0
            ? `Left. ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} recorded.`
            : 'Left the library.',
        )
      } else {
        toast.info('You were not in a library.')
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not leave.')
    } finally {
      // Leave the room whatever happened: staying on a screen whose every
      // call now 409s is worse than a redirect after a failed leave.
      navigate('/library', { replace: true })
    }
  }

  return (
    <div className={styles.rail}>
      <button
        type="button"
        className={styles.back}
        onClick={() => navigate('/library')}
      >
        ← All rooms
      </button>

      <div className={`${styles.timerBox} ${iAmMentor ? styles.duty : ''}`}>
        <span className={styles.label}>
          {/* A mentor is not accruing study time — their presence is
              supervision, and "your session" implies it counts towards
              something it does not. */}
          {iAmMentor ? 'On duty' : onBreak ? 'Paused' : 'Your session'}
        </span>
        <span className={styles.value}>{hoursMinutes(shown)}</span>
        {iAmMentor && (
          <span className={styles.note}>Students can see you are here</span>
        )}
      </div>

      <button type="button" className={styles.action} disabled>
        <span className={styles.icon} aria-hidden="true">
          ▣
        </span>
        Turn camera on
        <span className={styles.soon}>soon</span>
      </button>

      {/*
        *** NOT SHOWN TO A MENTOR ***
        A break holds a SEAT for thirty minutes. A mentor consumes no seat,
        so there is nothing to hold and the button would do nothing.
      */}
      {!iAmMentor &&
        (onBreak ? (
          <button
            type="button"
            className={`${styles.action} ${styles.onBreak}`}
            onClick={handleResume}
            disabled={endBreak.isPending}
          >
            <span className={styles.icon} aria-hidden="true">
              ☕
            </span>
            <span className={styles.breakText}>
              <span className={styles.breakLabel}>On a break</span>
              <span className={styles.breakClock}>
                {countdown(breakLeft ?? 0)} left
              </span>
            </span>
          </button>
        ) : (
          <button
            type="button"
            className={styles.action}
            onClick={handleBreak}
            disabled={startBreak.isPending}
          >
            <span className={styles.icon} aria-hidden="true">
              ☕
            </span>
            Take a break
          </button>
        ))}

      <div className={styles.spacer} />

      <button
        type="button"
        className={styles.leave}
        onClick={handleLeave}
        disabled={leave.isPending}
      >
        {leave.isPending ? 'Leaving…' : 'Leave the library'}
      </button>
    </div>
  )
}
